<template>
    <modal @close="onClose" ref="modal">
        <template v-slot:title>Add an authenticator app</template>

        <slot>
            <form-errors :errors="formErrors"/>

            <form v-if="!begun" @submit.prevent="onBegin" novalidate class="mui-form" data-test="totp-name">
                <p>Name this app so you can tell it apart later.</p>
                <form-input v-model="formData.name" :errors="errors.name" label="Name" placeholder="e.g. iPhone, 1Password" type="text" maxlength="100" :nocaps="true" />
                <button-row>
                    <button type="button" class="btn btn-outline-primary" @click.prevent="close">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="busy">Continue</button>
                </button-row>
            </form>

            <form v-else @submit.prevent="onConfirm" novalidate class="mui-form" data-test="totp-confirm">
                <p>Scan this code with your authenticator app, then enter the 6-digit code it shows.</p>
                <!-- Server-generated SVG from a URI we build; never put user text here -->
                <div class="text-center mb-2 totp-qr" v-html="begun.qr_svg"></div>
                <details class="mb-2">
                    <summary>Can't scan it?</summary>
                    <p class="mb-0">Enter this key in your app: <code data-test="totp-secret">{{ groupedSecret }}</code></p>
                </details>
                <form-input v-model="formData.code" :errors="errors.code" label="6-digit code" type="text" maxlength="7" :nocaps="true" />
                <button-row>
                    <button type="button" class="btn btn-outline-primary" @click.prevent="close">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="busy">Add app</button>
                </button-row>
            </form>
        </slot>
    </modal>
</template>

<script>
import api from '@/api'
import bus from '@/bus'
import ModalMixin from '@/mixins/ModalMixin'
import FormMixin from '@/mixins/Form'
import { report } from './mfaHelpers'

export default {
    mixins: [ModalMixin, FormMixin],
    props: {
        guard: {type: Function, required: true},
    },
    emits: ['added', 'close'],
    data() {
        return {
            begun: null,
            busy: false,
            formRules: {
                code: {presence: {allowEmpty: false}},
            },
            formData: this.makeFormData(),
        }
    },
    computed: {
        groupedSecret() {
            return ((this.begun && this.begun.secret) || '').match(/.{1,4}/g)?.join(' ') || ''
        },
    },
    methods: {
        makeFormData() {
            return {name: '', code: ''}
        },
        reset() {
            this.begun = null
            this.busy = false
            this.errors = {}
            this.formErrors = []
            this.formData = this.makeFormData()
        },
        onClose() {
            this.reset()
            this.$emit('close')
        },
        onBegin() {
            if (this.busy) {
                return
            }
            this.busy = true
            this.formErrors = []
            this.guard(() => api.post('/account/mfa/totp/begin', {name: this.formData.name.trim()})).then((resp) => {
                this.busy = false
                this.begun = resp
            }).catch((errors) => {
                this.busy = false
                this.formErrors = (errors && errors.__all__) || []
                report(errors)
            })
        },
        onConfirm() {
            if (this.busy || !this.validate(['code'])) {
                return
            }
            this.busy = true
            this.formErrors = []
            const id = this.begun.id
            this.guard(() => api.post(`/account/mfa/totp/${id}/confirm`, {code: this.formData.code.trim()})).then((resp) => {
                this.busy = false
                bus.showInfo('Authenticator app added.')
                this.$emit('added', resp)
                this.close()
            }).catch((errors) => {
                this.busy = false
                if (errors && errors.code) {
                    this.errors = errors
                } else {
                    this.formErrors = (errors && errors.__all__) || []
                    report(errors)
                }
            })
        },
    },
}
</script>

<style scoped>
.totp-qr :deep(svg) {
    max-width: 14rem;
    height: auto;
}
</style>
